import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import { contentApi } from '../services/api';

interface UploadFileModalProps {
  open: boolean;
  folder: string;
  onClose: () => void;
  onUploadSuccess: () => void;
}

export const UploadFileModal: FC<UploadFileModalProps> = ({
  open,
  folder,
  onClose,
  onUploadSuccess,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [fileStatuses, setFileStatuses] = useState<Record<number, 'pending' | 'uploading' | 'completed' | 'error'>>({});

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setError(null);
      setProgress(0);
      setFileStatuses({});
    }
  }, [open]);

  const validateFiles = (fileList: File[]) => {
    const validExtensions: Record<string, string[]> = {
      categories: ['csv'],
      courses: ['mbz'],
      competency_frameworks: ['csv', 'xml'],
      learning_path_templates: ['csv', 'xml'],
    };

    const allowed = validExtensions[folder] || [];
    const invalidFiles = fileList.filter(file => {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      return !fileExt || !allowed.includes(fileExt);
    });

    if (invalidFiles.length > 0) {
      setError(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Allowed: ${allowed.join(', ')}`);
      return false;
    }

    setError(null);
    return true;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0 && validateFiles(selectedFiles)) {
      setFiles(selectedFiles);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0 && validateFiles(droppedFiles)) {
      setFiles(droppedFiles);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setLoading(true);
    setError(null);
    setProgress(0);

    // Initialize all files as pending
    const initialStatuses: Record<number, 'pending' | 'uploading' | 'completed' | 'error'> = {};
    files.forEach((_, index) => {
      initialStatuses[index] = 'pending';
    });
    setFileStatuses(initialStatuses);

    try {
      // Upload files individually with progress tracking
      let completedFiles = 0;
      
      for (let i = 0; i < files.length; i++) {
        // Update status to uploading
        setFileStatuses(prev => ({ ...prev, [i]: 'uploading' }));
        
        try {
          // Upload single file - pass the File object directly
          const file = files[i];
          await contentApi.uploadFile(folder, file.name, file);
          
          // Mark as completed
          setFileStatuses(prev => ({ ...prev, [i]: 'completed' }));
          completedFiles++;
          
          // Update overall progress
          setProgress((completedFiles / files.length) * 100);
        } catch (fileError) {
          // Mark as error
          setFileStatuses(prev => ({ ...prev, [i]: 'error' }));
          console.error(`Failed to upload ${files[i].name}:`, fileError);
        }
      }
      
      // Check if all files completed successfully
      if (completedFiles === files.length) {
        // Short delay to show completion
        await new Promise(resolve => setTimeout(resolve, 1000));
        setFiles([]);
        onUploadSuccess();
        onClose();
      } else {
        setError(`${completedFiles}/${files.length} files uploaded successfully. Some files failed.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Upload File to {folder}</DialogTitle>
      <DialogContent sx={{ minWidth: 400, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: 'action.hover',
            mb: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'action.selected',
              borderColor: 'primary.dark',
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
          component="label"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            type="file"
            hidden
            multiple
            onChange={handleFileSelect}
            disabled={loading}
          />
          <CloudUploadIcon sx={{ fontSize: 56, color: '#1976d2' }} />
          <div style={{ fontWeight: 500, color: '#333' }}>
            {files.length > 0 
              ? files.length === 1 
                ? files[0].name 
                : `${files.length} files selected`
              : 'Click to select or drag files here'
            }
          </div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>
            {folder === 'categories' && 'Supported: .csv'}
            {folder === 'courses' && 'Supported: .mbz'}
            {(folder === 'competency_frameworks' || folder === 'learning_path_templates') && 'Supported: .csv, .xml'}
          </div>
        </Box>
        
        {files.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              {loading ? 'Upload Progress:' : 'Selected Files:'}
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
              <List dense sx={{ py: 0 }}>
                {files.map((file, index) => {
                  const status = fileStatuses[index] || 'pending';
                  let icon;
                  let color: string = 'inherit';
                  
                  switch (status) {
                    case 'completed':
                      icon = <CheckCircleIcon color="success" />;
                      color = '#2e7d32';
                      break;
                    case 'uploading':
                      icon = <PendingIcon color="primary" sx={{ 
                        '@keyframes pulse': {
                          '0%': { opacity: 1 },
                          '50%': { opacity: 0.5 },
                          '100%': { opacity: 1 },
                        },
                        animation: 'pulse 1.5s infinite' 
                      }} />;
                      color = '#1976d2';
                      break;
                    case 'error':
                      icon = <ErrorIcon color="error" />;
                      color = '#d32f2f';
                      break;
                    default:
                      icon = <PendingIcon color="disabled" />;
                      color = '#666';
                  }
                  
                  return (
                    <ListItem key={index} sx={{ py: 0.5, px: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ color }}>
                            {file.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="textSecondary">
                            {(file.size / 1024).toFixed(1)} KB
                            {status === 'uploading' && ' - Uploading...'}
                            {status === 'completed' && ' - Complete'}
                            {status === 'error' && ' - Failed'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
        )}
        
        {progress > 0 && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={files.length === 0 || loading}
        >
          {loading 
            ? `Uploading... ${Math.round(progress)}%` 
            : files.length === 1 
              ? 'Upload File' 
              : `Upload ${files.length} Files`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};
